import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, Database, ShieldAlert, FileText, Globe, Lock } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { LeakItem } from '@/components/leaks/LeakCard';
import { Helmet } from 'react-helmet';

export default function LeakDetails() {
    const { domain } = useParams<{ domain: string }>();
    const { language, t } = useLanguage();
    const navigate = useNavigate();
    const [leak, setLeak] = useState<LeakItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLeak = async () => {
            setLoading(true);
            try {
                const { data, error: downloadError } = await supabase
                    .storage
                    .from('dane')
                    .download('leak.json');

                if (downloadError) throw downloadError;

                const text = await data.text();
                const leaksData: LeakItem[] = JSON.parse(text);

                // Sort by breach date (newest first) - CRITICAL: Must match LeaksFeed sorting
                const sortedLeaks = leaksData.sort((a, b) =>
                    new Date(b.breach_date).getTime() - new Date(a.breach_date).getTime()
                );

                // Find leak by domain or fallback ID
                let foundLeak = sortedLeaks.find(l => l.domain === domain);

                // Fallback logic for "unknown-" IDs
                if (!foundLeak && domain?.startsWith('unknown-')) {
                    const index = parseInt(domain.replace('unknown-', ''), 10);
                    if (!isNaN(index) && sortedLeaks[index]) {
                        foundLeak = sortedLeaks[index];
                    }
                }

                if (foundLeak) {
                    setLeak(foundLeak);
                } else {
                    setError(t('leaks.not_found') || 'Leak not found');
                }
            } catch (err) {
                console.error('Error fetching leak:', err);
                setError(t('common.error') || 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchLeak();
    }, [domain, t]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Navbar />
                <main className="flex-grow container py-8">
                    <Skeleton className="h-12 w-48 mb-8" />
                    <Skeleton className="h-64 w-full rounded-xl" />
                </main>
                <Footer />
            </div>
        );
    }

    if (error || !leak) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Navbar />
                <main className="flex-grow container py-8 flex flex-col items-center justify-center text-center">
                    <ShieldAlert className="h-16 w-16 text-destructive mb-4" />
                    <h1 className="text-2xl font-bold mb-2">{error || 'Leak not found'}</h1>
                    <Button onClick={() => navigate('/leaks')} variant="outline">
                        {t('common.back')}
                    </Button>
                </main>
                <Footer />
            </div>
        );
    }

    // Strip HTML from description and handle localization
    const rawDescription = language === 'pl' && leak.description_pl ? leak.description_pl : leak.description;
    const description = rawDescription ? rawDescription.replace(/<[^>]*>/g, '') : '';

    // Handle data types localization
    const rawDataTypes = language === 'pl' && leak.data_leaked_pl ? leak.data_leaked_pl : leak.data_leaked;
    const dataTypes = rawDataTypes ? rawDataTypes.split(',').map(t => t.trim()).filter(Boolean) : [];

    return (
        <div className="min-h-screen flex flex-col font-sans selection:bg-red-500/30">
            <Helmet>
                <title>{leak.domain || 'Unknown'} | CyberMonit Leaks</title>
            </Helmet>
            <Navbar />

            <main className="flex-grow container py-8 pt-24 px-4 md:px-6 relative z-10">
                <Button
                    onClick={() => navigate(-1)}
                    variant="outline"
                    className="mb-6 border-primary/20 hover:bg-primary/10 hover:text-primary transition-colors"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('common.back')}
                </Button>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-1 lg:grid-cols-3 gap-8"
                >
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="rounded-xl border border-red-500/20 bg-black/40 backdrop-blur-md p-6 md:p-8 shadow-[0_0_30px_-10px_rgba(220,38,38,0.2)] relative overflow-hidden group">

                            <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 via-transparent to-transparent pointer-events-none" />

                            <div className="relative z-10">
                                <div className="flex items-start justify-between mb-6">
                                    <div>
                                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tight flex items-center gap-3">
                                            {leak.domain || 'Unknown Target'}
                                            {leak.domain && <Globe className="h-6 w-6 text-red-500 opacity-80" />}
                                        </h1>
                                        <div className="flex items-center gap-4 text-muted-foreground">
                                            <span className="flex items-center gap-1.5 text-sm">
                                                <Calendar className="h-4 w-4" />
                                                {new Date(leak.breach_date).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US')}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-sm">
                                                <Database className="h-4 w-4" />
                                                {/* Source is not in LeakItem interface, removing or using static text if needed */}
                                                {'Verified Source'}
                                            </span>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="border-red-500/50 text-red-400 bg-red-500/10 px-3 py-1 text-sm animate-pulse">
                                        VERIFIED LEAK
                                    </Badge>
                                </div>

                                <div className="prose prose-invert max-w-none">
                                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-red-100">
                                        <FileText className="h-5 w-5 text-red-500" />
                                        {t('leaks.description') || 'Description'}
                                    </h3>
                                    <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-wrap font-light">
                                        {description || t('leaks.no_description') || 'No description available.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Stats */}
                    <div className="space-y-6">
                        <div className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm p-6 sticky top-24">
                            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Lock className="h-5 w-5 text-primary" />
                                {t('leaks.breach_stats') || 'Breach Statistics'}
                            </h3>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-4 rounded-lg bg-secondary/20 border border-white/5">
                                    <span className="text-muted-foreground">{t('leaks.records') || 'Total Records'}</span>
                                    <span className="font-mono text-xl font-bold text-primary">
                                        {new Intl.NumberFormat().format(leak.leak_count)}
                                    </span>
                                </div>

                                <div className="p-4 rounded-lg bg-secondary/20 border border-white/5">
                                    <span className="text-muted-foreground block mb-3">{t('leaks.data_types') || 'Compromised Data'}</span>
                                    <div className="flex flex-wrap gap-2">
                                        {dataTypes.length > 0 ? (
                                            dataTypes.map((type, i) => (
                                                <Badge
                                                    key={i}
                                                    variant="outline"
                                                    className="bg-red-500/5 border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors"
                                                >
                                                    {type}
                                                </Badge>
                                            ))
                                        ) : (
                                            <span className="text-sm text-muted-foreground italic">
                                                {t('common.no_data') || 'No data types listed'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </main>
            <Footer />
            <div className="fixed bottom-0 right-0 p-1 text-[10px] text-muted-foreground opacity-50 pointer-events-none">v3.0</div>
        </div>
    );
}
